// Jobs — every job in the week as one table: date, time, customer, tech, service,
// status, $. Sortable columns, filters as one row of chips, no cards. A row opens
// the customer window on its Appointments tab. Won-quote projects (jobs_app) keep
// their own chip and columns, and still open their project page. On the phone
// the table becomes a list and a row opens a sheet.
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import useIsPhone from "../../../hooks/useIsPhone";
import { DataTable } from "../../ui/table";
import Badge from "../../ui/badge";
import SchedHeader from "./SchedHeader";
import JobSheet from "./JobSheet";
import useRangeBoard, { flattenStops, shiftIso, todayIso, weekDays, weekStartOf, weekRangeLabel } from "./useRangeBoard";
import { listJobs } from "../../../api/jobsApi";
import { formatCurrency } from "../dispatch/utils";
import { serviceFamily } from "../dispatch/serviceFamily";
import { getDispatchVocab, capitalize } from "../dispatch/dispatchVocab";
import { getIndustryKey } from "../../../constants/uiRegistry";
import "../RoutePlanner.css";
import "../dispatch/dispatch-b.css";
import "./sched-c.css";

const CustomerPopup = lazy(() => import("../../Profile/CustomerPopup"));
// Won-quote projects live in jobs_app, which only the trades verticals use.
const PROJECT_INDUSTRIES = new Set(["landscaping", "construction", "roofing", "general"]);

const statusOf = (s) => {
  const v = String(s?.status || "").toLowerCase();
  if (/complet|done|finished/.test(v)) return "done";
  if (/cancel|skip|void/.test(v)) return "canceled";
  return "scheduled";
};
const STATUS = { scheduled: ["Scheduled", "neutral"], done: ["Done", "success"], canceled: ["Canceled", "danger"] };
const PROJECT_STATUS = { draft: "Draft", pending: "Pending", active: "Active", on_hold: "On hold", completed: "Completed", canceled: "Canceled" };
const money = (v) => (v == null || v === "" ? "—" : formatCurrency(v));

export default function JobsView() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const vocab = useMemo(() => getDispatchVocab(), []);
  const [start, setStart] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("date");
    return weekStartOf(/^\d{4}-\d{2}-\d{2}$/.test(p || "") ? p : todayIso());
  });
  const days = useMemo(() => weekDays(start), [start]);
  const { routes, loading, error } = useRangeBoard(days[0], days[6]);
  const [chip, setChip] = useState("all");
  const [job, setJob] = useState(null);
  const [popup, setPopup] = useState(null);
  const [projects, setProjects] = useState(null);

  // Projects only where the vertical has them — a 404 elsewhere is noise, not data.
  useEffect(() => {
    if (!PROJECT_INDUSTRIES.has(getIndustryKey("general"))) { setProjects([]); return undefined; }
    let alive = true;
    listJobs().then(({ data }) => { if (alive) setProjects(Array.isArray(data) ? data : data?.results || []); }).catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => flattenStops(routes).map((s) => ({ ...s, st: statusOf(s), when: `${s.serviceDay} ${s.computedStart || "99:99"}` })), [routes]);
  const counts = useMemo(() => rows.reduce((a, r) => {
    a.all += 1; a[r.st] += 1; if (!r.technicianName) a.unassigned += 1; return a;
  }, { all: 0, scheduled: 0, done: 0, canceled: 0, unassigned: 0 }), [rows]);
  const visible = useMemo(() => rows.filter((r) => chip === "all" || (chip === "unassigned" ? !r.technicianName : r.st === chip)), [rows, chip]);
  const value = visible.reduce((n, r) => n + Number(r.value || 0), 0);

  const chips = [
    ["all", "All", counts.all],
    ["unassigned", vocab.unassigned, counts.unassigned],
    ["scheduled", "Scheduled", counts.scheduled],
    ["done", "Done", counts.done],
    counts.canceled ? ["canceled", "Canceled", counts.canceled] : null,
    projects && projects.length ? ["projects", "Projects", projects.length] : null,
  ].filter(Boolean);

  const open = (r) => {
    if (isPhone) { setJob(r); return; }
    if (r.customerId) { setPopup(r); return; }
    navigate(`/schedule/day?date=${r.serviceDay}`);
  };

  const columns = [
    { key: "when", label: "When", render: (r) => <span className="sc-cell-dim">{new Date(`${r.serviceDay}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "numeric", day: "numeric" })} · {r.computedStart || "—"}</span> },
    { key: "customerName", label: "Customer", render: (r) => <span className="sc-cell-name"><i className="sc-rail" style={{ "--pin": serviceFamily(r.serviceName, r.serviceCode).color }} />{r.customerName}</span> },
    { key: "technicianName", label: capitalize(vocab.worker), sortValue: (r) => r.technicianName || null, render: (r) => r.technicianName || <span className="sc-cell-dim">{vocab.unassigned}</span> },
    { key: "serviceName", label: capitalize(vocab.service), sortValue: (r) => r.serviceName || r.serviceCode || null, render: (r) => <span title={r.serviceName}>{r.serviceName || r.serviceCode || "—"}</span> },
    { key: "st", label: "Status", render: (r) => <Badge tone={STATUS[r.st][1]}>{STATUS[r.st][0]}</Badge> },
    { key: "value", label: "$", num: true, sortValue: (r) => Number(r.value || 0), render: (r) => (r.value ? formatCurrency(r.value) : "—") },
  ];
  const projectColumns = [
    { key: "job_number", label: "Job", render: (p) => <span className="sc-cell-name">{p.job_number} {p.title}</span> },
    { key: "customer_name", label: "Customer", render: (p) => p.customer_name || "—" },
    { key: "status", label: "Status", render: (p) => <Badge tone={p.status === "completed" ? "success" : p.status === "canceled" ? "danger" : "neutral"}>{PROJECT_STATUS[p.status] || p.status}</Badge> },
    { key: "next_visit", label: "Next visit", render: (p) => (p.next_visit ? new Date(p.next_visit).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—") },
    { key: "quote_price", label: "Price", num: true, sortValue: (p) => Number(p.quote_price || 0), render: (p) => money(p.quote_price) },
    { key: "running_margin", label: "Margin", num: true, sortValue: (p) => Number(p.running_margin || 0), render: (p) => money(p.running_margin) },
  ];

  const weekLabel = weekRangeLabel(days[0], days[6]);

  let body;
  if (chip === "projects") {
    body = isPhone ? (
      <div className="sc-list">
        {(projects || []).map((p) => (
          <button key={p.id} type="button" className="sc-row sc-row--day" onClick={() => navigate(`/schedule/jobs/${p.id}`)}>
            <span className="sc-row-main"><b>{p.job_number} {p.title}</b><span>{p.customer_name || "—"} · {PROJECT_STATUS[p.status] || p.status}</span></span>
            <span className="sc-row-value">{money(p.quote_price)}</span>
            <ChevronRight size={14} className="sc-row-chev" aria-hidden="true" />
          </button>
        ))}
      </div>
    ) : (
      <div className="sc-table-wrap"><DataTable columns={projectColumns} rows={projects || []} onRowClick={(p) => navigate(`/schedule/jobs/${p.id}`)} empty="No projects yet. Convert an accepted quote and it lands here." /></div>
    );
  } else if (isPhone) {
    body = (
      <div className="sc-list">
        {visible.sort((a, b) => a.when.localeCompare(b.when)).map((r) => (
          <button key={`${r.routeId}-${r.id}`} type="button" className="sc-row" style={{ "--pin": serviceFamily(r.serviceName, r.serviceCode).color }} onClick={() => open(r)}>
            <span className="sc-row-time">{new Date(`${r.serviceDay}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" })}<br />{r.computedStart || ""}</span>
            <span className="sc-row-main"><b>{r.customerName}</b><span>{r.technicianName || vocab.unassigned} · {r.serviceName || r.serviceCode || "—"}</span></span>
            <span className="sc-row-value">{r.value ? formatCurrency(r.value) : ""}</span>
            <ChevronRight size={14} className="sc-row-chev" aria-hidden="true" />
          </button>
        ))}
        {!visible.length ? <div className="sc-loading">No {vocab.stops} match this filter in {weekLabel}.</div> : null}
      </div>
    );
  } else {
    body = (
      <div className="sc-table-wrap">
        <DataTable columns={columns} rows={visible} rowKey={(r) => `${r.routeId}-${r.id}`} onRowClick={open} initialSort={{ key: "when", dir: "asc" }} empty={`No ${vocab.stops} match this filter in ${weekLabel}.`} />
      </div>
    );
  }

  return (
    <div className="dispatch-shell sc-screen">
      <SchedHeader
        label={weekLabel}
        onPrev={() => setStart((s) => shiftIso(s, -7))}
        onNext={() => setStart((s) => shiftIso(s, 7))}
        prevLabel="Previous week"
        nextLabel="Next week"
        totals={loading || chip === "projects" ? [] : [`${visible.length} ${vocab.stops}`, formatCurrency(value)]}
        menu={[{ label: "This week", run: () => setStart(weekStartOf(todayIso())) }]}
      />
      <div className="sc-chips" role="toolbar" aria-label="Filter jobs">
        {chips.map(([k, l, n]) => (
          <button key={k} type="button" className={`sc-chip${chip === k ? " is-active" : ""}`} aria-pressed={chip === k} onClick={() => setChip(k)}>
            {l} <b>{n}</b>
          </button>
        ))}
      </div>
      {error ? <div className="sc-inline-error" role="alert">{error}</div> : null}
      {loading && chip !== "projects" ? <div className="sc-loading">Loading the week…</div> : body}
      {isPhone ? <JobSheet job={job} vocab={vocab} onClose={() => setJob(null)} /> : null}
      {popup ? (
        <Suspense fallback={null}>
          <CustomerPopup
            lead={{ object: "customer", id: popup.customerId, customer_id: popup.customerId, display_name: popup.customerName }}
            leadType="customer"
            initialTab="appointments"
            focusAppointment={popup.crmAppointmentId || popup.id}
            boardStartLabel={popup.computedStart || null}
            onClose={() => setPopup(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
